import { apiScopes, msalInstance } from '@/auth/msalConfig';
import TeamsSDK from '@/teams/TeamsSDK';
import type {
  FundamentalCatalogResponse,
  FundamentalCatalogItem,
  DailyHabitCatalogItem,
  AttitudeBehaviorCatalogItem,
  SurveyListResponse,
  PagedSurveyResponse,
  PagedSurveyDto,
  SurveyItem,
  PendingInstancesResponse,
  SubmitResponseResult,
  CreateSurveyRequest,
  CreateSurveyResponse,
  AnalyticsSummaryResponse,
  FundamentalsResponse,
  HeatmapResponse,
  OnaResponse,
  GenerationalResponse,
  GenerationalParticipantsResponse,
  BiasResponse,
  OmissionsResponse,
  PersonalResponse,
  PersonalGrowthResponse,
  MyStatsResponse,
  TipsResponse,
  TipItem,
  ActionsResponse,
  ActionItem,
  UserListResponse,
  UserItem,
  OUListResponse,
  OUItem,
  OUTreeResponse,
  OURootsResponse,
  OuCatalogCacheStatusDto,
  JobsListResponse,
  JobRunsListResponse,
  JobRunDto,
  JobSlug,
  RulesListResponse,
  RuleItem,
  NotificationSettingsDto,
  SendGeneralNotificationRequest,
  SendGeneralNotificationResult,
  CreateUserApiRequest,
  SurveyExportData,
} from './types';

// Driven by VITE_USE_MOCK in .env.development — true = use mock data, false = real APIs
export const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true';

import { pushDebugEntry } from '@/debug/debugStore';

function _safeParseBody(body: BodyInit | null | undefined): unknown {
  if (typeof body !== 'string') return null;
  try { return JSON.parse(body); } catch { return body; }
}

const BASE_URLS = {
  survey:       import.meta.env.VITE_API_SURVEY_URL       ?? '',
  analytics:    import.meta.env.VITE_API_ANALYTICS_URL    ?? '',
  userAdmin:    import.meta.env.VITE_API_USERS_URL         ?? '',
  rules:        import.meta.env.VITE_API_RULES_URL         ?? '',
  meeting:      import.meta.env.VITE_API_MEETING_URL       ?? '',
  notifications: import.meta.env.VITE_API_NOTIFICATIONS_URL ?? '',
};

async function getToken(): Promise<string> {
  // Dentro de Teams: SSO silencioso (getAuthToken). El token (aud=a2d1655c) lo aceptan las APIs
  // directo, sin OBO, porque comparten audiencia. No usa redirect (que se bloquea en el iframe).
  if (TeamsSDK.isInTeams()) {
    try {
      return await TeamsSDK.getSsoToken();
    } catch (e) {
      // Visible SIEMPRE (no solo en DEV) para diagnosticar por qué Teams SSO no da token.
      console.error('[Auth] Teams SSO getAuthToken falló:', e);
      return '';
    }
  }
  // Navegador standalone: MSAL con la instancia COMPARTIDA (ya inicializada + redirect
  // procesado en AuthProvider). Crear una nueva instancia aquí no veía la sesión.
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    console.warn('[Auth] MSAL sin cuenta activa todavía — token vacío (401 esperado hasta que complete el login)');
    return '';
  }
  try {
    const result = await msalInstance.acquireTokenSilent({ scopes: apiScopes, account });
    return result.accessToken;
  } catch (e) {
    console.error('[Auth] acquireTokenSilent falló:', e);
    return '';
  }
}

function getDevRole(): string {
  try {
    const stored = localStorage.getItem('sigma-demo-user');
    if (!stored) return 'Employee';
    const parsed = JSON.parse(stored) as { role?: string };
    return parsed.role ?? 'Employee';
  } catch {
    return 'Employee';
  }
}

async function request<T>(
  service: keyof typeof BASE_URLS,
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getToken();
  const correlationId = crypto.randomUUID();

  // ── DEV: capture for DebugPanel ────────────────────────────────────────────
  const _isDbg   = import.meta.env.VITE_DEMO_MODE;
  const _startMs = _isDbg ? performance.now() : 0;
  const _method  = (options.method ?? 'GET').toUpperCase();
  const _reqBody = _isDbg ? _safeParseBody(options.body) : null;
  const _url     = `${BASE_URLS[service]}${path}`;

  let res: Response;
  try {
    res = await fetch(_url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Correlation-Id': correlationId,
        // In dev, tell the backend which role the demo user has selected.
        // DevAuthMiddleware reads this and sets claims accordingly.
        ...(import.meta.env.DEV ? { 'X-Dev-Role': getDevRole() } : {}),
        ...(options.headers ?? {}),
      },
    });
  } catch (networkErr) {
    if (_isDbg) pushDebugEntry({ id: correlationId, timestamp: Date.now(), method: _method,
      service, path, url: _url, status: null, durationMs: Math.round(performance.now() - _startMs),
      requestBody: _reqBody, responseBody: String(networkErr), isError: true });
    throw networkErr;
  }
  // ───────────────────────────────────────────────────────────────────────────

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    if (_isDbg) pushDebugEntry({ id: correlationId, timestamp: Date.now(), method: _method,
      service, path, url: _url, status: res.status, durationMs: Math.round(performance.now() - _startMs),
      requestBody: _reqBody, responseBody: body, isError: true });
    throw new ApiError(res.status, body);
  }

  if (res.status === 204) {
    if (_isDbg) pushDebugEntry({ id: correlationId, timestamp: Date.now(), method: _method,
      service, path, url: _url, status: 204, durationMs: Math.round(performance.now() - _startMs),
      requestBody: _reqBody, responseBody: null, isError: false });
    return undefined as unknown as T;
  }

  const data = await res.json() as T;
  if (_isDbg) pushDebugEntry({ id: correlationId, timestamp: Date.now(), method: _method,
    service, path, url: _url, status: res.status, durationMs: Math.round(performance.now() - _startMs),
    requestBody: _reqBody, responseBody: data, isError: false });
  return data;
}

async function requestUpload<T>(
  service: keyof typeof BASE_URLS,
  path: string,
  file: File,
): Promise<T> {
  const token = await getToken();
  const correlationId = crypto.randomUUID();
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(`${BASE_URLS[service]}${path}`, {
    method: 'POST',
    body: formData,
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Correlation-Id': correlationId,
      ...(import.meta.env.DEV ? { 'X-Dev-Role': getDevRole() } : {}),
      // Sin 'Content-Type': el navegador lo fija con el boundary correcto de multipart/form-data.
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body);
  }

  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(public status: number, public body: unknown) {
    super(`API error ${status}`);
  }
}

export const catalogApi = {
  getFundamentals: () => request<FundamentalCatalogResponse>('survey', '/v1/catalog/fundamentals'),

  createFundamental: (body: { names: Record<string, string>; shortNames?: Record<string, string>; shortName?: string; color?: string; descriptions?: Record<string, string>; description?: string; icon?: string; url?: string }) =>
    request<FundamentalCatalogItem>('survey', '/v1/catalog/fundamentals', {
      method: 'POST', body: JSON.stringify(body),
    }),
  updateFundamental: (id: string, body: { names: Record<string, string>; shortNames?: Record<string, string>; shortName?: string; color?: string; descriptions?: Record<string, string>; description?: string; icon?: string; url?: string }) =>
    request<FundamentalCatalogItem>('survey', `/v1/catalog/fundamentals/${id}`, {
      method: 'PUT', body: JSON.stringify(body),
    }),
  deleteFundamental: (id: string) =>
    request<{ success: boolean }>('survey', `/v1/catalog/fundamentals/${id}`, { method: 'DELETE' }),

  createDailyHabit: (fundamentalId: string, body: { names: Record<string, string>; attitudeBehaviorId?: string }) =>
    request<DailyHabitCatalogItem>('survey', `/v1/catalog/fundamentals/${fundamentalId}/habits`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  updateDailyHabit: (fundamentalId: string, habitId: string, body: { names: Record<string, string> }) =>
    request<DailyHabitCatalogItem>('survey', `/v1/catalog/fundamentals/${fundamentalId}/habits/${habitId}`, {
      method: 'PUT', body: JSON.stringify(body),
    }),
  deleteDailyHabit: (fundamentalId: string, habitId: string) =>
    request<{ success: boolean }>('survey', `/v1/catalog/fundamentals/${fundamentalId}/habits/${habitId}`, {
      method: 'DELETE',
    }),

  createAttitude: (fundamentalId: string, body: { names: Record<string, string>; descriptions?: Record<string, string> }) =>
    request<AttitudeBehaviorCatalogItem>('survey', `/v1/catalog/fundamentals/${fundamentalId}/attitudes`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  updateAttitude: (fundamentalId: string, attitudeId: string, body: { names: Record<string, string>; descriptions?: Record<string, string> }) =>
    request<AttitudeBehaviorCatalogItem>('survey', `/v1/catalog/fundamentals/${fundamentalId}/attitudes/${attitudeId}`, {
      method: 'PUT', body: JSON.stringify(body),
    }),
  deleteAttitude: (fundamentalId: string, attitudeId: string) =>
    request<{ success: boolean }>('survey', `/v1/catalog/fundamentals/${fundamentalId}/attitudes/${attitudeId}`, {
      method: 'DELETE',
    }),

  moveFundamental: (id: string, direction: 'up' | 'down') =>
    request<FundamentalCatalogItem>('survey', `/v1/catalog/fundamentals/${id}/move`, {
      method: 'PUT', body: JSON.stringify({ direction }),
    }),
  moveAttitude: (fundamentalId: string, attitudeId: string, direction: 'up' | 'down') =>
    request<AttitudeBehaviorCatalogItem>('survey', `/v1/catalog/fundamentals/${fundamentalId}/attitudes/${attitudeId}/move`, {
      method: 'PUT', body: JSON.stringify({ direction }),
    }),
  moveDailyHabit: (fundamentalId: string, habitId: string, direction: 'up' | 'down') =>
    request<DailyHabitCatalogItem>('survey', `/v1/catalog/fundamentals/${fundamentalId}/habits/${habitId}/move`, {
      method: 'PUT', body: JSON.stringify({ direction }),
    }),

  uploadFundamentalImage: (id: string, file: File) =>
    requestUpload<FundamentalCatalogItem>('survey', `/v1/catalog/fundamentals/${id}/image`, file),
  uploadAttitudeImage: (fundamentalId: string, attitudeId: string, file: File) =>
    requestUpload<AttitudeBehaviorCatalogItem>('survey', `/v1/catalog/fundamentals/${fundamentalId}/attitudes/${attitudeId}/image`, file),
  uploadDailyHabitImage: (fundamentalId: string, habitId: string, file: File) =>
    requestUpload<DailyHabitCatalogItem>('survey', `/v1/catalog/fundamentals/${fundamentalId}/habits/${habitId}/image`, file),
};

function formatPeriod(startDate?: string, expiresAt?: string): string {
  const fmt = (iso: string) => {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  };
  if (startDate && expiresAt) return `${fmt(startDate)} → ${fmt(expiresAt)}`;
  if (startDate) return `${fmt(startDate)} →`;
  if (expiresAt) return `→ ${fmt(expiresAt)}`;
  return '—';
}

function mapBackendSurveyStatus(backendStatus: string): SurveyItem['status'] {
  switch (backendStatus) {
    case 'Active':   return 'active';
    case 'Paused':   return 'paused';
    case 'Draft':    return 'pending_approval';
    case 'Cancelled': return 'cancelled';
    case 'Expired':
    case 'Archived':
    default:         return 'inactive';
  }
}

async function fetchSurveys(status?: string): Promise<SurveyListResponse> {
  const path = status ? `/v1/surveys?status=${status}` : '/v1/surveys';
  const paged = await request<PagedSurveyResponse>('survey', path);
  return (paged.items ?? []).map(s => ({
    id: s.id,
    name: s.name,
    status: mapBackendSurveyStatus(s.status),
    audience: s.ouIds?.join(', ') ?? '',
    questions: s.questions?.length ?? 0,
    responses: s.responseCount ?? 0,
    period: formatPeriod(s.startDate, s.expiresAt),
    periodStart: s.startDate,
    periodEnd: s.expiresAt,
    audienceType: s.audienceType,
    createdBy: s.createdBy,
    isApproved: s.isApproved,
    deletedAt: s.deletedAt,
    deletedBy: s.deletedBy,
    category: s.category,
  }));
}

export const surveyApi = {
  getSurveys:           ()                       => fetchSurveys(),
  getDeletedSurveys:    ()                       => fetchSurveys('Cancelled'),
  getPendingInstances:  (tenantId: string, userId?: string) =>
    request<PendingInstancesResponse>('survey', `/v1/survey-instances/pending?tenantId=${tenantId}${userId ? `&userId=${encodeURIComponent(userId)}` : ''}`),
  getMyStats:           (userId?: string)         => request<MyStatsResponse>('survey', `/v1/survey-instances/my-stats${userId ? `?userId=${encodeURIComponent(userId)}` : ''}`),
  submitResponse:       (instanceId: string, answers: Array<{ questionId: string; questionType: string; score: number | null; openText: string | null }>, userId?: string) =>
    request<SubmitResponseResult>('survey', `/v1/responses/${instanceId}${userId ? `?userId=${encodeURIComponent(userId)}` : ''}`, { method: 'POST', body: JSON.stringify({ surveyInstanceId: instanceId, answers }) }),
  omitInstance:         (instanceId: string, userId?: string) =>
    request<{ success: boolean }>('survey', `/v1/responses/${instanceId}/omit${userId ? `?userId=${encodeURIComponent(userId)}` : ''}`, { method: 'POST' }),
  createSurvey:         (body: CreateSurveyRequest) =>
    request<CreateSurveyResponse>('survey', '/v1/surveys', { method: 'POST', body: JSON.stringify(body) }),
  deleteSurvey:         (id: string)             => request<void>('survey', `/v1/surveys/${id}`, { method: 'DELETE' }),
  // Single survey (for edit mode)
  getSurveyById:        (id: string) => request<PagedSurveyDto>('survey', `/v1/surveys/${id}`),
  // Templates
  getTemplates:         (page = 1, pageSize = 20) =>
    request<PagedSurveyResponse>('survey', `/v1/surveys/templates?page=${page}&pageSize=${pageSize}`),
  duplicateTemplate:    (id: string)             =>
    request<CreateSurveyResponse>('survey', `/v1/surveys/templates/${id}/duplicate`, { method: 'POST' }),
  deleteTemplate:       (id: string)             => request<void>('survey', `/v1/surveys/templates/${id}`, { method: 'DELETE' }),
  // Tips
  getTips:              (fundamentalId?: string) =>
    request<TipsResponse>('survey', `/v1/tips${fundamentalId ? `?fundamentalId=${fundamentalId}` : ''}`),
  createTip:            (body: Omit<TipItem, 'id'>) =>
    request<TipItem>('survey', '/v1/tips', { method: 'POST', body: JSON.stringify(body) }),
  updateTip:            (id: string, body: Omit<TipItem, 'id'>) =>
    request<TipItem>('survey', `/v1/tips/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteTip:            (id: string)             => request<void>('survey', `/v1/tips/${id}`, { method: 'DELETE' }),
  // Actions
  getActions:           (fundamentalId?: string) =>
    request<ActionsResponse>('survey', `/v1/actions${fundamentalId ? `?fundamentalId=${fundamentalId}` : ''}`),
  createAction:         (body: Omit<ActionItem, 'id'>) =>
    request<ActionItem>('survey', '/v1/actions', { method: 'POST', body: JSON.stringify(body) }),
  updateAction:         (id: string, body: Omit<ActionItem, 'id'>) =>
    request<ActionItem>('survey', `/v1/actions/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteAction:         (id: string)             => request<void>('survey', `/v1/actions/${id}`, { method: 'DELETE' }),
  // Survey CRUD extras
  updateSurvey: (id: string, body: { name: string; description?: string; category?: string; startDate?: string; expiresAt?: string; questions: unknown[]; campaignWeight?: number; activeDays?: number[]; boostUrgente?: boolean; boostDays?: number; minRespondents?: number; maxRespondents?: number; audienceType?: string; ouIds?: string[]; targetUserIds?: string[]; targetMeetingId?: string; nameTranslations?: Record<string, string>; descriptionTranslations?: Record<string, string> }) =>
    request<CreateSurveyResponse>('survey', `/v1/surveys/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  restoreSurvey: (id: string) =>
    request<CreateSurveyResponse>('survey', `/v1/surveys/${id}/restore`, { method: 'POST' }),
  permanentDeleteSurvey: (id: string) =>
    request<void>('survey', `/v1/surveys/${id}/permanent`, { method: 'DELETE' }),
  patchSurveyStatus: (id: string, action: 'activate' | 'pause') =>
    request<CreateSurveyResponse>('survey', `/v1/surveys/${id}/status`, { method: 'PATCH', body: JSON.stringify({ action }) }),
  createTemplate: (body: Omit<CreateSurveyRequest, 'isGlobalAdmin'>) =>
    request<CreateSurveyResponse>('survey', '/v1/surveys/templates', { method: 'POST', body: JSON.stringify(body) }),
  saveAsTemplate: (id: string) =>
    request<CreateSurveyResponse>('survey', `/v1/surveys/${id}/save-as-template`, { method: 'POST' }),
  duplicateSurvey: (id: string) =>
    request<CreateSurveyResponse>('survey', `/v1/surveys/${id}/duplicate`, { method: 'POST' }),
  exportSurvey: (id: string, lang = 'en') =>
    request<SurveyExportData>('survey', `/v1/surveys/${id}/export?lang=${encodeURIComponent(lang)}`),
  recalculateResponseCounts: () =>
    request<{ surveysUpdated: number }>('survey', '/v1/surveys/recalculate-responses', { method: 'POST' }),
};

export interface AnalyticsFilterParams {
  ouId?: string;
  startDate?: string; // ISO date string
  endDate?: string;   // ISO date string
  language?: string;  // BCP-47 base tag, e.g. "es", "en", "pt"
}

function buildAnalyticsQs(params: AnalyticsFilterParams): string {
  const parts: string[] = [];
  if (params.ouId)      parts.push(`ouId=${encodeURIComponent(params.ouId)}`);
  if (params.startDate) parts.push(`startDate=${encodeURIComponent(params.startDate)}`);
  if (params.endDate)   parts.push(`endDate=${encodeURIComponent(params.endDate)}`);
  if (params.language)  parts.push(`lang=${encodeURIComponent(params.language)}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

export const analyticsApi = {
  getSummary:      (filters: AnalyticsFilterParams = {}) => request<AnalyticsSummaryResponse>('analytics', `/v1/analytics/summary${buildAnalyticsQs(filters)}`),
  getFundamentals: (filters: AnalyticsFilterParams = {}) => request<FundamentalsResponse>('analytics', `/v1/analytics/fundamentals${buildAnalyticsQs(filters)}`),
  getHeatmap:      (filters: AnalyticsFilterParams = {}) => request<HeatmapResponse>('analytics', `/v1/analytics/heatmap${buildAnalyticsQs(filters)}`),
  getOna:          (filters: AnalyticsFilterParams = {}) => request<OnaResponse>('analytics', `/v1/analytics/ona${buildAnalyticsQs(filters)}`),
  getGenerational: (filters: AnalyticsFilterParams = {}) => request<GenerationalResponse>('analytics', `/v1/analytics/generational${buildAnalyticsQs(filters)}`),
  getGenerationalParticipants: (filters: AnalyticsFilterParams = {}) => request<GenerationalParticipantsResponse>('analytics', `/v1/analytics/generational/participants${buildAnalyticsQs(filters)}`),
  getBias:         (filters: AnalyticsFilterParams = {}) => request<BiasResponse>('analytics', `/v1/analytics/bias${buildAnalyticsQs(filters)}`),
  getOmissions:    (filters: AnalyticsFilterParams = {}) => request<OmissionsResponse>('analytics', `/v1/analytics/omissions${buildAnalyticsQs(filters)}`),
  getPersonal:       (userId?: string) => request<PersonalResponse>('analytics', `/v1/analytics/personal${userId ? `?userId=${encodeURIComponent(userId)}` : ''}`),
  getPersonalGrowth: (userId?: string) => request<PersonalGrowthResponse>('analytics', `/v1/analytics/personal/growth${userId ? `?userId=${encodeURIComponent(userId)}` : ''}`),
};

export const userAdminApi = {
  getUsers: ({ tenantId, page = 1, pageSize = 20, ouId, search }: {
    tenantId: string;
    page?: number;
    pageSize?: number;
    ouId?: string;
    search?: string;
  }) => {
    const params = new URLSearchParams({ tenantId, page: String(page), pageSize: String(pageSize) });
    if (ouId) params.set('ouId', ouId);
    if (search) params.set('search', search);
    return request<UserListResponse>('userAdmin', `/v1/users?${params.toString()}`);
  },
  getUser:    (id: string) => request<UserItem>('userAdmin', `/v1/users/${id}`),
  createUser: (body: CreateUserApiRequest) =>
    request<UserItem>('userAdmin', '/v1/users', { method: 'POST', body: JSON.stringify(body) }),
  updateUser: (id: string, body: { ouId?: string; preferredLanguage?: string }) =>
    request<UserItem>('userAdmin', `/v1/users/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  /** Auto-servicio: el propio usuario cambia su idioma preferido (sin permisos de Admin). */
  updateOwnLanguage: (id: string, preferredLanguage: string) =>
    request<UserItem>('userAdmin', `/v1/users/${id}/language`, {
      method: 'PATCH', body: JSON.stringify({ preferredLanguage }),
    }),
  updateRoles:(id: string, body: { roleKey: string }) =>
    request<UserItem>('userAdmin', `/v1/users/${id}/roles`, { method: 'PUT', body: JSON.stringify(body) }),
  toggleUserActive: (id: string, isActive: boolean) =>
    request<UserItem>('userAdmin', `/v1/users/${id}/active`, { method: 'PATCH', body: JSON.stringify({ isActive }) }),
  updateUserRoles: (id: string, roles: string[]) =>
    request<UserItem>('userAdmin', `/v1/users/${id}/roles`, { method: 'PUT', body: JSON.stringify({ roles }) }),
  updateUserPermissions: (id: string, perms: { canCreateSurveys: boolean; canViewAnalytics: boolean; canManageUsers: boolean; canApproveSurveys: boolean }) =>
    request<UserItem>('userAdmin', `/v1/users/${id}/permissions`, { method: 'PATCH', body: JSON.stringify(perms) }),
  deleteUser: (id: string) => request<void>('userAdmin', `/v1/users/${id}`, { method: 'DELETE' }),
  getOUs:       (tenantId: string) => request<OUListResponse>('userAdmin', `/v1/organizational-units?tenantId=${tenantId}`),
  getOUTree:    (ouId: string)    => request<OUTreeResponse>('userAdmin', `/v1/organizational-units/${ouId}/tree`),
  getOURoots:   ()                => request<OURootsResponse>('userAdmin', '/v1/organizational-units/roots'),
  getOuCatalogCacheStatus: ()     => request<OuCatalogCacheStatusDto>('userAdmin', '/v1/organizational-units/cache-status'),
  getJobs: () => request<JobsListResponse>('userAdmin', '/v1/jobs'),
  getJobRuns: (jobName: JobSlug, limit = 30) =>
    request<JobRunsListResponse>('userAdmin', `/v1/jobs/${jobName}/runs?limit=${limit}`),
  triggerJob: (jobName: JobSlug) =>
    request<JobRunDto>('userAdmin', `/v1/jobs/${jobName}/run`, { method: 'POST' }),
  createOU: (body: { name: string; description?: string; type?: string; gdpr?: boolean; timezone?: string; azureAdGroupId?: string }) =>
    request<OUItem>('userAdmin', '/v1/organizational-units', { method: 'POST', body: JSON.stringify(body) }),
  updateOU: (ouId: string, body: { name?: string; description?: string; type?: string; gdpr?: boolean; timezone?: string; azureAdGroupId?: string }) =>
    request<OUItem>('userAdmin', `/v1/organizational-units/${ouId}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteOU: (ouId: string) =>
    request<void>('userAdmin', `/v1/organizational-units/${ouId}`, { method: 'DELETE' }),
  setOuActive:  (ouId: string, isActive: boolean) =>
    request<OUItem>('userAdmin', `/v1/organizational-units/${ouId}/active`, {
      method: 'PATCH', body: JSON.stringify({ isActive }),
    }),
  /** Resuelve displayName en lote para una lista de userIds. Usado en flujo de meetings. */
  batchLookupUsers: (userIds: string[]) =>
    request<Array<{ id: string; displayName: string }>>('userAdmin', '/v1/users/batch-lookup', {
      method: 'POST', body: JSON.stringify({ userIds }),
    }),
};

export const rulesApi = {
  getRules:   ()                           => request<RulesListResponse>('rules', '/v1/rules'),
  updateRule: (code: string, body: Omit<RuleItem, 'code'>) =>
    request<RuleItem>('rules', `/v1/rules/${code}`, { method: 'PUT', body: JSON.stringify(body) }),
  upsertRuleOuOverride: (code: string, ouId: string, value: string | null) =>
    request<RuleItem>('rules', `/v1/rules/${code}/ou-overrides/${encodeURIComponent(ouId)}`, {
      method: 'PATCH', body: JSON.stringify({ value }),
    }),
};

export interface SaveOmitirSettingsRequest {
  omitirEnabled: boolean;
  omitirThreshold: number;
  omitirNotifyUser: boolean;
  omitirNotifyManager: boolean;
  omitirNotifyAdminOU: boolean;
  omitirUserTemplates: Record<string, string>;
  omitirMgrTemplates: Record<string, string>;
  omitirOuOverrides: Array<{ ouId: string; language: string; userMessage: string; managerMessage: string }>;
}

export interface SaveEuropaSettingsRequest {
  europaEnabled: boolean;
  europaUserTemplates: Record<string, string>;
  europaMgrTemplates: Record<string, string>;
}

export const notificationsApi = {
  getSettings: () =>
    request<NotificationSettingsDto>('notifications', '/v1/notifications/settings'),
  saveSettings: (body: NotificationSettingsDto) =>
    request<void>('notifications', '/v1/notifications/settings', {
      method: 'PUT', body: JSON.stringify(body),
    }),
  saveOmitirSettings: (body: SaveOmitirSettingsRequest) =>
    request<void>('notifications', '/v1/notifications/settings/omitir', {
      method: 'PUT', body: JSON.stringify(body),
    }),
  saveEuropaSettings: (body: SaveEuropaSettingsRequest) =>
    request<void>('notifications', '/v1/notifications/settings/europa', {
      method: 'PUT', body: JSON.stringify(body),
    }),
  sendGeneral: (body: SendGeneralNotificationRequest) =>
    request<SendGeneralNotificationResult>('notifications', '/v1/notifications/general', {
      method: 'POST', body: JSON.stringify(body),
    }),
};

/**
 * Perfil (roles + idioma preferido) del usuario actual, tomado de UserAdminService (gestionado
 * en la app, NO en Azure AD salvo por la sincronización inicial de Graph). `ResolveByEmail`
 * devuelve el UserDto con roles y preferredLanguage.
 * Fail-soft: devuelve roles=[] y preferredLanguage=undefined si no se puede resolver (usuario
 * no existe / servicio caído) → el portal lo trata como Employee sin forzar idioma.
 */
export async function getCurrentUserProfile(
  email: string,
): Promise<{ roles: string[]; preferredLanguage?: string }> {
  if (USE_MOCK || !email) return { roles: [] };
  try {
    const dto = await request<{ roles?: string[]; preferredLanguage?: string }>(
      'userAdmin',
      `/v1/users/resolve?email=${encodeURIComponent(email)}`,
    );
    return { roles: dto?.roles ?? [], preferredLanguage: dto?.preferredLanguage };
  } catch (error) {
    if (import.meta.env.DEV) console.error('getCurrentUserProfile failed, defaulting to Employee/no-language', error);
    return { roles: [] };
  }
}
